/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { UserRole } from '../types/auth';

/**
 * Backend Contract Specification for PHARMA TRACE (Fase 4)
 * Standardizes server-side operations, input validation, output structures and correlation.
 */

export interface BaseBackendRequest {
  correlationId: string;
  requesterUid: string;
  requesterEmail?: string;
  tenantId: string;
  role: UserRole;
  clientTimestamp: string;
}

export interface BaseBackendResponse<T = any> {
  correlationId: string;
  success: boolean;
  serverTimestamp: string;
  data?: T;
  errorCode?: string;
  errorMessage?: string;
}

// 1. Critical Restore Contract
export interface RestoreBackupRequest extends BaseBackendRequest {
  backupId: string;
  expectedSha256: string;
  targetTenantId: string;
  reason: string;
  overrideExistingData?: boolean;
}

export interface RestoreBackupResponse extends BaseBackendResponse<{
  restoredOrdenesCount: number;
  restoredDisposicionesCount: number;
  restoredUsersCount: number;
  verifiedSha256: string;
}> {}

// 2. Critical Hard-Delete Contract
export interface HardDeleteRequest extends BaseBackendRequest {
  resourceType: 'orden' | 'disposicion' | 'backup' | 'user';
  resourceId: string;
  justification: string;
  legalRetentionConfirmed: boolean;
}

export interface HardDeleteResponse extends BaseBackendResponse<{
  purgedResourceId: string;
  purgedAt: string;
}> {}

// 3. Controlled Migration Contract
export interface ExecuteMigrationRequest extends BaseBackendRequest {
  migrationId: string;
  targetVersion: string;
  idempotencyKey: string;
  dryRun?: boolean;
}

export interface ExecuteMigrationResponse extends BaseBackendResponse<{
  migrationId: string;
  affectedDocumentsCount: number;
  dryRun: boolean;
  executionLogs: string[];
}> {}

// 4. Audit Log Event Contract
export interface ServerAuditEvent {
  correlationId: string;
  actor: {
    uid: string;
    email?: string;
    nombreCompleto: string;
    role: UserRole;
    companyId: string;
  };
  operation: string;
  modulo: string;
  recurso: string;
  recursoId?: string;
  resultado: 'EXITO' | 'FALLO' | 'DENEGADO';
  detalles: string;
  metadata?: Record<string, any>;
  serverTimestamp: string;
}

/**
 * Generates a standard cryptographically unique correlation ID
 */
export function generateCorrelationId(prefix: string = 'EVT'): string {
  const dateStr = new Date().toISOString().replace(/[-:T.Z]/g, '').substring(0, 14);
  const randSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${prefix}-${dateStr}-${randSuffix}`;
}
